import {Record as ImmutableRecord, Set, Map, List} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';
import * as MessageComposerActions from 'web-app/react/inbox/inbox-message-composer/actions';

import * as Actions from './actions';
import {MESSAGES_PER_PAGE} from './helpers';
import type {MessageSelectionMode} from './constants';

interface Message {
    messageId: string;
}

export class ConversationState extends ImmutableRecord<{
    loading: boolean;
    paginatedLoading: boolean;
    shouldShowEdit: boolean;
    shouldShowForward: boolean;
    messageSelectionMode: MessageSelectionMode;
    editingOwnMessage: boolean;
    preselectedMessage: string;
    markedMessages: Set<string>;
    messagesToForward: Message[];
    conversation: Map<string, any>;
    fetching: boolean;
}>({
    loading: false,
    paginatedLoading: false,
    shouldShowEdit: false,
    shouldShowForward: false,
    messageSelectionMode: 'forwarding',
    editingOwnMessage: false,
    preselectedMessage: '',
    markedMessages: Set(),
    messagesToForward: [],
    conversation: Map(),
    fetching: false,
}) {}

const reset = () => new ConversationState();

const hasMoreMessages = (response: {messages?: Message[]}): boolean =>
    (response.messages?.length ?? 0) >= MESSAGES_PER_PAGE;

export default makeReducer<ConversationState>(
    [
        match(Actions.clearConversation, () => reset()),

        match(Actions.leaveConversationSuccess, () => reset()),

        match(Actions.showEditBar, (state, action) =>
            state.merge({
                shouldShowEdit:
                    action.payload && action.payload.visibility !== undefined
                        ? action.payload.visibility
                        : !state.shouldShowEdit,
            }),
        ),

        match(Actions.toggleForwardView, (state, action) =>
            state.merge({
                messageSelectionMode: 'forwarding',
                shouldShowForward: action.payload.show,
                messagesToForward: [],
                shouldShowEdit: false,
            }),
        ),

        match(Actions.setMessageSelectionMode, (state, action) =>
            state.merge({
                messageSelectionMode: action.payload.messageSelectionMode,
            }),
        ),

        match(Actions.setMessageForDeletion, (state, action) =>
            state.merge({
                markedMessages: action.payload.shouldMark
                    ? state.markedMessages.add(action.payload.messageId)
                    : state.markedMessages.delete(action.payload.messageId),
            }),
        ),

        match(Actions.setMessageToBeForwarded, (state, action) =>
            state.merge({
                messagesToForward: action.payload.shouldMark
                    ? [...state.messagesToForward, action.payload.message]
                    : state.messagesToForward.filter(message => message.messageId !== action.payload.message.messageId),
            }),
        ),

        match(Actions.deleteMessages, state =>
            state.merge({
                loading: true,
            }),
        ),

        match(Actions.deleteMessagesSuccess, (state, action) => {
            const updatedMessages = state.conversation.get('messages').filter((message: Message) => {
                return !action.payload.messageIds.includes(message.messageId);
            });
            const updatedConversation = state.conversation.merge({messages: updatedMessages});

            return new ConversationState({
                conversation: updatedConversation,
            });
        }),

        match(Actions.deleteMessagesFailed, () => reset()),

        match(Actions.deleteMessagesAborted, state =>
            state.merge({
                loading: false,
            }),
        ),

        match(Actions.fetchConversation, (state, action) =>
            state.merge({
                fetching: !action.payload.reload,
            }),
        ),

        match(Actions.fetchConversationSuccess, (state, action) =>
            action.payload.wasReload
                ? state.merge({
                      fetching: false,
                      conversation: state.conversation
                          .merge(Map(action.payload.response) satisfies Map<string, any>)
                          .set('messages', List(action.payload.response.messages))
                          .set('cursor', action.payload.response.cursor)
                          .set('hasMoreMsgs', hasMoreMessages(action.payload.response)),
                  })
                : new ConversationState({
                      conversation: (Map(action.payload.response) satisfies Map<string, any>)
                          .set('messages', List(action.payload.response.messages))
                          .set('cursor', action.payload.response.cursor)
                          .set('hasMoreMsgs', hasMoreMessages(action.payload.response)),
                      fetching: false,
                  }),
        ),

        match(Actions.fetchConversationFailed, state =>
            state.merge({
                fetching: false,
            }),
        ),

        match(Actions.fetchPagedConversation, state => state.merge({paginatedLoading: true})),

        match(Actions.fetchPagedConversationSuccess, (state, action) => {
            const currentMessages = state.conversation.has('messages') ? state.conversation.get('messages') : List();
            return state.merge({
                paginatedLoading: false,
                conversation: state.conversation
                    .merge(Map(action.payload.response) satisfies Map<string, any>)
                    .set('messages', currentMessages.unshift(...action.payload.response.messages))
                    .set('cursor', action.payload.response.cursor)
                    .set('hasMoreMsgs', hasMoreMessages(action.payload.response)),
            });
        }),

        match(Actions.fetchPagedConversationFailed, state => state.merge({paginatedLoading: false})),

        match(Actions.fetchAfterMessageId, (state, action) =>
            state.merge({
                fetching: !action.payload.reload,
            }),
        ),

        match(Actions.fetchAfterMessageIdSuccess, (state, action) =>
            action.payload.wasReload
                ? state.merge({
                      fetching: false,
                      conversation: state.conversation
                          .merge(Map(action.payload.response) satisfies Map<string, any>)
                          .set('messages', List(action.payload.response.messages))
                          .set('cursor', action.payload.response.cursor)
                          .set('hasMoreMsgs', hasMoreMessages(action.payload.response)),
                  })
                : new ConversationState({
                      conversation: (Map(action.payload.response) satisfies Map<string, any>)
                          .set('messages', List(action.payload.response.messages))
                          .set('cursor', action.payload.response.cursor)
                          .set('hasMoreMsgs', hasMoreMessages(action.payload.response)),
                      fetching: false,
                  }),
        ),

        match(Actions.fetchAfterMessageIdFailed, state =>
            state.merge({
                fetching: false,
            }),
        ),

        match(Actions.archiveConversation, state =>
            state.merge({
                loading: true,
            }),
        ),

        match(Actions.archiveConversationSuccess, () => reset()),

        match(Actions.archiveConversationFailed, () => reset()),

        match(Actions.archiveConversationAborted, state =>
            state.merge({
                loading: false,
            }),
        ),

        match(Actions.unarchiveConversationSuccess, () => reset()),

        match(Actions.unarchiveConversationFailed, () => reset()),

        match(MessageComposerActions.replySuccess, (state, action) => {
            if (!state.conversation.get('messages')) {
                return state;
            }

            const updatedMessages = state.conversation.get('messages').concat(action.payload.newMessage);
            const updatedConversation = state.conversation.merge({messages: updatedMessages, loginReads: []});

            return new ConversationState({
                conversation: updatedConversation,
            });
        }),

        match(Actions.renameConversation, (state, action) => {
            if (state.conversation.get('conversationId') !== action.payload.conversationId) {
                return state;
            }

            return state.merge({conversation: state.conversation.merge({title: action.payload.title})});
        }),
    ],
    new ConversationState(),
);
